import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Seat } from '../../repositories/SeatLayoutRepository';

interface SeatCanvasProps {
  seats: Seat[];
  canvasWidth: number;
  canvasHeight: number;
  snapGrid: number;
  onSeatMove: (seatId: string, newX: number, newY: number) => void;
  onSeatClick: (seatId: string) => void;
  selectedSeatId?: string;
}

interface DragState {
  seatId: string;
  startX: number;
  startY: number;
  offsetX: number;
  offsetY: number;
}

const SeatCanvas: React.FC<SeatCanvasProps> = ({
  seats,
  canvasWidth,
  canvasHeight,
  snapGrid,
  onSeatMove,
  onSeatClick,
  selectedSeatId
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [dragState, setDragState] = useState<DragState | null>(null);
  const [hoveredSeatId, setHoveredSeatId] = useState<string | null>(null);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; seatId: string } | null>(null);

  const snapToGrid = useCallback((value: number): number => {
    if (snapGrid === 0) return value;
    return Math.round(value / snapGrid) * snapGrid;
  }, [snapGrid]);

  const getSeatAtPosition = useCallback((x: number, y: number): Seat | null => {
    for (let i = seats.length - 1; i >= 0; i--) {
      const seat = seats[i];
      const rect = {
        x: seat.position_x,
        y: seat.position_y,
        width: seat.width,
        height: seat.height
      };

      if (x >= rect.x && x <= rect.x + rect.width &&
          y >= rect.y && y <= rect.y + rect.height) {
        return seat;
      }
    }
    return null;
  }, [seats]);

  const drawGrid = useCallback((ctx: CanvasRenderingContext2D) => {
    if (snapGrid === 0) return;

    ctx.strokeStyle = '#e5e7eb';
    ctx.lineWidth = 1;
    ctx.setLineDash([2, 2]);

    for (let x = 0; x <= canvasWidth; x += snapGrid) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, canvasHeight);
      ctx.stroke();
    }

    for (let y = 0; y <= canvasHeight; y += snapGrid) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(canvasWidth, y);
      ctx.stroke();
    }

    ctx.setLineDash([]);
  }, [canvasWidth, canvasHeight, snapGrid]);

  const drawSeat = useCallback((
    ctx: CanvasRenderingContext2D,
    seat: Seat,
    isSelected: boolean,
    isHovered: boolean
  ) => {
    ctx.save();

    const centerX = seat.position_x + seat.width / 2;
    const centerY = seat.position_y + seat.height / 2;

    ctx.translate(centerX, centerY);
    ctx.rotate((seat.rotation * Math.PI) / 180);
    ctx.translate(-centerX, -centerY);

    if (seat.is_assigned) {
      ctx.fillStyle = '#10b981';
    } else {
      ctx.fillStyle = '#6b7280';
    }

    if (isHovered) {
      ctx.fillStyle = '#3b82f6';
    }

    ctx.fillRect(seat.position_x, seat.position_y, seat.width, seat.height);

    if (isSelected) {
      ctx.strokeStyle = '#2563eb';
      ctx.lineWidth = 3;
    } else {
      ctx.strokeStyle = '#374151';
      ctx.lineWidth = 1;
    }
    ctx.strokeRect(seat.position_x, seat.position_y, seat.width, seat.height);

    ctx.fillStyle = '#ffffff';
    ctx.font = '12px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(seat.seat_number, centerX, centerY);

    ctx.restore();
  }, []);

  const render = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvasWidth, canvasHeight);

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);

    drawGrid(ctx);

    seats.forEach(seat => {
      const isSelected = seat.id === selectedSeatId;
      const isHovered = seat.id === hoveredSeatId;
      drawSeat(ctx, seat, isSelected, isHovered);
    });
  }, [seats, canvasWidth, canvasHeight, selectedSeatId, hoveredSeatId, drawGrid, drawSeat]);

  useEffect(() => {
    render();
  }, [render]);

  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const seat = getSeatAtPosition(x, y);
    if (seat) {
      setDragState({
        seatId: seat.id,
        startX: seat.position_x,
        startY: seat.position_y,
        offsetX: x - seat.position_x,
        offsetY: y - seat.position_y
      });
      onSeatClick(seat.id);
    } else {
      onSeatClick(-1);
    }
  }, [getSeatAtPosition, onSeatClick]);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    if (dragState) {
      const newX = snapToGrid(x - dragState.offsetX);
      const newY = snapToGrid(y - dragState.offsetY);

      const clampedX = Math.max(0, Math.min(newX, canvasWidth - 60));
      const clampedY = Math.max(0, Math.min(newY, canvasHeight - 40));

      const currentSeat = seats.find(s => s.id === dragState.seatId);
      if (currentSeat && (currentSeat.position_x !== clampedX || currentSeat.position_y !== clampedY)) {
        onSeatMove(dragState.seatId, clampedX, clampedY);
      }
    } else {
      const seat = getSeatAtPosition(x, y);
      setHoveredSeatId(seat?.id || null);
      canvas.style.cursor = seat ? 'move' : 'default';
    }
  }, [dragState, snapToGrid, canvasWidth, canvasHeight, seats, onSeatMove, getSeatAtPosition]);

  const handleMouseUp = useCallback(() => {
    setDragState(null);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setDragState(null);
    setHoveredSeatId(null);
    if (canvasRef.current) {
      canvasRef.current.style.cursor = 'default';
    }
  }, []);

  const handleDoubleClick = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const seat = getSeatAtPosition(x, y);
    if (seat) {
      onSeatClick(seat.id);
    }
  }, [getSeatAtPosition, onSeatClick]);

  const handleContextMenu = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    e.preventDefault();

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const seat = getSeatAtPosition(x, y);
    if (seat) {
      setContextMenu({
        x: e.clientX,
        y: e.clientY,
        seatId: seat.id
      });
      onSeatClick(seat.id);
    } else {
      setContextMenu(null);
    }
  }, [getSeatAtPosition, onSeatClick]);

  useEffect(() => {
    const handleClickOutside = () => setContextMenu(null);
    if (contextMenu) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [contextMenu]);

  return (
    <div className="relative inline-block">
      <canvas
        ref={canvasRef}
        width={canvasWidth}
        height={canvasHeight}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onDoubleClick={handleDoubleClick}
        onContextMenu={handleContextMenu}
        className="border-2 border-gray-300 dark:border-gray-600 rounded-lg shadow-lg bg-white"
        style={{ maxWidth: '100%', height: 'auto' }}
      />

      {contextMenu && (
        <div
          className="fixed bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1 z-50"
          style={{ left: contextMenu.x, top: contextMenu.y }}
        >
          <button
            onClick={() => {
              onSeatClick(contextMenu.seatId);
              setContextMenu(null);
            }}
            className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            속성 보기
          </button>
          <button
            onClick={() => {
              const seat = seats.find(s => s.id === contextMenu.seatId);
              if (seat) {
                const newRotation = (seat.rotation + 90) % 360;
                onSeatMove(seat.id, seat.position_x, seat.position_y);
              }
              setContextMenu(null);
            }}
            className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            90도 회전
          </button>
          <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>
          <button
            onClick={() => {
              setContextMenu(null);
            }}
            className="w-full px-4 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
          >
            삭제 (Del)
          </button>
        </div>
      )}

      <div className="mt-4 flex items-center gap-6 text-sm text-gray-600 dark:text-gray-400">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-gray-500 rounded"></div>
          <span>빈 좌석</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-green-500 rounded"></div>
          <span>배정됨</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-blue-500 rounded"></div>
          <span>마우스 오버</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 border-2 border-blue-600 rounded"></div>
          <span>선택됨</span>
        </div>
      </div>

      <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
        <p>드래그: 좌석 이동 | 더블클릭: 속성 편집 | 우클릭: 메뉴</p>
        {snapGrid > 0 && <p>스냅 그리드: {snapGrid}px</p>}
      </div>
    </div>
  );
};

export default SeatCanvas;
